import type { ReactNode } from "react";
import { Icon } from "@iconify/react";

export function HeroPreview() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-3 shadow-[0_30px_80px_-35px_rgba(43,54,36,0.45)] sm:p-5">
      <div className="absolute inset-0 bg-gradient-to-br from-[#EBE6DA] via-[#E3DED0] to-[#D8D2C4] opacity-75" />

      <div className="relative lg:hidden">
        <MobileHeroPreview />
      </div>

      <div className="relative hidden overflow-x-auto rounded-xl border border-white/50 bg-[#F9F7F2]/95 shadow-2xl lg:block">
        <div className="min-w-[62rem]">
          <div className="flex h-12 items-center justify-between border-b border-[#2B3624]/10 bg-white/90 px-4">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#D6A27C]" />
                <span className="h-2 w-2 rounded-full bg-[#D7C96A]" />
                <span className="h-2 w-2 rounded-full bg-[#7C8D52]" />
              </div>
              <span className="h-4 w-px bg-[#2B3624]/10" />
              <span className="text-xs font-semibold text-[#2B3624]">Northstar Launch</span>
              <span className="rounded-full bg-[#E6EBCB] px-2 py-0.5 text-xs font-medium text-[#516039]">
                Live
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {["M", "J", "A"].map((initial, i) => (
                  <span
                    key={initial}
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-xs font-medium text-white ${
                      i === 0 ? "bg-[#C8A58B]" : i === 1 ? "bg-[#7C8D9B]" : "bg-[#8B9277]"
                    }`}
                  >
                    {initial}
                  </span>
                ))}
              </div>
              <div className="flex h-8 w-40 items-center gap-2 rounded-md border border-[#2B3624]/10 bg-[#F6F3EC] px-3 text-xs text-[#8A9185]">
                <Icon icon="solar:magnifer-linear" className="text-sm" />
                Search workspace
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[9rem_1fr_16rem]">
            <aside className="border-r border-[#2B3624]/10 bg-[#F1EEE6] p-3">
              <p className="mb-3 px-2 text-xs font-medium uppercase tracking-wider text-[#93998E]">
                Workspace
              </p>
              <div className="space-y-1 text-xs font-medium text-[#687064]">
                <div className="flex items-center gap-2 rounded-md bg-white px-2.5 py-2 text-[#2B3624] shadow-sm">
                  <Icon icon="solar:chat-round-dots-linear" className="text-base" />
                  Workspace
                </div>
                {[
                  ["solar:book-2-linear", "Knowledge"],
                  ["solar:videocamera-record-linear", "Sessions"],
                  ["solar:flag-linear", "Decisions"],
                  ["solar:calendar-linear", "Calendar"],
                ].map(([icon, label]) => (
                  <div key={label} className="flex items-center gap-2 px-2.5 py-2">
                    <Icon icon={icon} className="text-base" />
                    {label}
                  </div>
                ))}
                <div className="flex items-center justify-between px-2.5 py-2">
                  <span className="flex items-center gap-2">
                    <Icon icon="solar:checklist-minimalistic-linear" className="text-base" />
                    Tasks
                  </span>
                  <span className="rounded-full bg-[#E3E7D0] px-1.5 text-xs">5</span>
                </div>
              </div>
            </aside>

            <div className="bg-white">
              <div className="flex h-11 items-center justify-between border-b border-[#2B3624]/10 px-4">
                <div>
                  <p className="text-xs font-semibold text-[#2B3624]">Launch planning</p>
                  <p className="text-xs text-[#93998E]">8 members · 12 files</p>
                </div>
                <span className="flex items-center gap-1.5 rounded-md bg-[#2B3624] px-3 py-1.5 text-xs font-medium text-white">
                  <Icon icon="solar:videocamera-record-linear" className="text-sm" />
                  Join call
                </span>
              </div>

              <div className="grid grid-cols-[1fr_12rem] border-b border-[#2B3624]/10">
                <div className="h-72 p-4">
                  <div className="space-y-4">
                    <MessageBubble initial="M" name="Maya Chen" time="10:42" color="bg-[#C8A58B]">
                      The client approved the launch direction. We still need final campaign assets before
                      Friday.
                    </MessageBubble>
                    <MessageBubble initial="J" name="Jon Bell" time="10:44" color="bg-[#7C8D9B]">
                      Should we keep the pilot in August or move it to September?
                    </MessageBubble>
                    <div className="flex gap-2.5">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#2B3624] text-white">
                        <Icon icon="solar:stars-minimalistic-linear" className="text-sm" />
                      </span>
                      <div className="max-w-md">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-[#2B3624]">Northstar AI</span>
                          <span className="rounded bg-[#E6EBCB] px-1.5 py-0.5 text-xs font-medium text-[#586343]">
                            AI
                          </span>
                        </div>
                        <div className="mt-1 rounded-lg border border-[#DDE2C9] bg-[#F4F6E9] px-3 py-2 text-xs leading-relaxed text-[#4B5563]">
                          Based on today&apos;s discussion, the team agreed to move the pilot launch to
                          September. I added the decision and created two follow-up tasks.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-l border-[#2B3624]/10 bg-[#20291D] p-2.5 text-white">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-400" />
                      <span className="text-xs font-medium">00:24:18</span>
                    </div>
                    <span className="text-xs text-white/50">Live transcript</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      ["MC", "Maya", "bg-[#64715E]"],
                      ["JB", "Jon", "bg-[#596A73]"],
                    ].map(([initials, name, bg]) => (
                      <div key={name} className={`relative h-20 overflow-hidden rounded-md ${bg}`}>
                        <div className="absolute inset-0 flex items-center justify-center text-lg font-medium">
                          {initials}
                        </div>
                        <span className="absolute bottom-1 left-1 rounded bg-black/30 px-1 text-xs">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <aside className="border-l border-[#2B3624]/10 bg-[#F8F6F1]">
              <div className="border-b border-[#2B3624]/10 p-3">
                <p className="text-xs font-semibold text-[#2B3624]">Live Workspace Intelligence</p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-[#7C8D52]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#7C8D52]" />
                  Updating from session
                </p>
              </div>
              <div className="space-y-3 p-3">
                <IntelCard title="Finalize launch message" meta="Maya · Friday" />
                <IntelCard title="Approve campaign assets" meta="Client · Pending" />
                <div className="rounded-lg border border-[#A8B284]/50 bg-[#EFF3DE] p-3">
                  <p className="text-xs font-medium uppercase tracking-wider text-[#718052]">Decision</p>
                  <p className="mt-1 text-xs font-medium text-[#2B3624]">Move pilot launch to September</p>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
      <p className="relative mt-4 text-center text-xs font-medium uppercase tracking-widest text-[#8B9285]">
        One workspace · One project memory · One aligned team
      </p>
    </div>
  );
}

function MobileHeroPreview() {
  return (
    <div className="overflow-hidden rounded-xl border border-white/50 bg-[#F9F7F2]/95 shadow-2xl">
      <div className="flex h-11 items-center justify-between border-b border-[#2B3624]/10 bg-white/90 px-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#2B3624]">Northstar Launch</span>
          <span className="rounded-full bg-[#E6EBCB] px-2 py-0.5 text-xs font-medium text-[#516039]">Live</span>
        </div>
        <div className="flex -space-x-2">
          {["M", "J", "A"].map((initial, i) => (
            <span
              key={initial}
              className={`flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-xs font-medium text-white ${
                i === 0 ? "bg-[#C8A58B]" : i === 1 ? "bg-[#7C8D9B]" : "bg-[#8B9277]"
              }`}
            >
              {initial}
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-4 bg-white p-4">
        <MessageBubble initial="M" name="Maya Chen" time="10:42" color="bg-[#C8A58B]">
          The client approved the launch direction. We still need final campaign assets before Friday.
        </MessageBubble>
        <div className="flex gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#2B3624] text-white">
            <Icon icon="solar:stars-minimalistic-linear" className="text-sm" />
          </span>
          <div className="max-w-md">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#2B3624]">Northstar AI</span>
              <span className="rounded bg-[#E6EBCB] px-1.5 py-0.5 text-xs font-medium text-[#586343]">AI</span>
            </div>
            <div className="mt-1 rounded-lg border border-[#DDE2C9] bg-[#F4F6E9] px-3 py-2 text-xs leading-relaxed text-[#4B5563]">
              Based on today&apos;s discussion, the team agreed to move the pilot launch to September. I added
              the decision and created two follow-up tasks.
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[#2B3624]/10 bg-[#F8F6F1] p-3">
        <p className="mb-2 text-xs font-semibold text-[#2B3624]">Live Workspace Intelligence</p>
        <div className="space-y-2">
          <IntelCard title="Finalize launch message" meta="Maya · Friday" />
          <div className="rounded-lg border border-[#A8B284]/50 bg-[#EFF3DE] p-3">
            <p className="text-xs font-medium uppercase tracking-wider text-[#718052]">Decision</p>
            <p className="mt-1 text-xs font-medium text-[#2B3624]">Move pilot launch to September</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  initial,
  name,
  time,
  color,
  children,
}: {
  initial: string;
  name: string;
  time: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-2.5">
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${color}`}
      >
        {initial}
      </span>
      <div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#2B3624]">{name}</span>
          <span className="text-xs text-[#A0A59C]">{time}</span>
        </div>
        <p className="mt-1 max-w-sm rounded-r-lg rounded-bl-lg bg-[#F3F0E9] px-3 py-2 text-xs leading-relaxed text-[#4B5563]">
          {children}
        </p>
      </div>
    </div>
  );
}

function IntelCard({ title, meta }: { title: string; meta: string }) {
  return (
    <div className="rounded-lg border border-[#2B3624]/10 bg-white p-3 shadow-sm">
      <div className="flex gap-2">
        <span className="mt-0.5 h-3.5 w-3.5 rounded border border-[#2B3624]/20" />
        <div>
          <p className="text-xs font-medium text-[#2B3624]">{title}</p>
          <p className="mt-1 text-xs text-[#92988E]">{meta}</p>
        </div>
      </div>
    </div>
  );
}
